import React from 'react';
import {
  Card,
  CardContent,
  Box,
  Typography,
  Avatar,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Tooltip
} from '@mui/material';
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Email as EmailIcon,
  Phone as PhoneIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { LeadershipAssignment } from '@/services/OrganizationSetupService';

interface LeaderCardProps {
  leader: LeadershipAssignment;
  canEdit: boolean;
  onEdit?: (leader: LeadershipAssignment) => void;
  onDelete?: (leader: LeadershipAssignment) => void;
}

const getPositionLevelLabel = (level: number): string => {
  switch (level) {
    case 1: return 'C-Suite';
    case 2: return 'Vice President';
    case 3: return 'Director';
    case 4: return 'Manager';
    default: return 'Leadership';
  }
};

const getInitials = (name: string): string => {
  return name
    .split(' ')
    .map(n => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const LeaderCard: React.FC<LeaderCardProps> = ({
  leader,
  canEdit,
  onEdit,
  onDelete
}) => {
  const theme = useTheme();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    onEdit?.(leader);
  };

  const handleDelete = () => {
    handleMenuClose();
    onDelete?.(leader);
  };

  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 3,
        border: `1px solid ${theme.palette.divider}`,
        position: 'relative',
        overflow: 'visible',
        transition: 'all 0.3s ease',
        '&:hover': {
          transform: 'translateY(-4px)',
          boxShadow: theme.shadows[8],
          borderColor: leader.color_code
        }
      }}
    >
      {/* Position Level Badge */}
      <Chip
        label={leader.position_code}
        size="small"
        sx={{
          position: 'absolute',
          top: -10,
          left: 16,
          backgroundColor: leader.color_code,
          color: 'white',
          fontWeight: 600,
          fontSize: '0.7rem'
        }}
      />

      {/* Actions Menu */}
      {canEdit && (
        <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
          <IconButton
            size="small"
            onClick={handleMenuOpen}
            sx={{
              backgroundColor: theme.palette.background.paper,
              '&:hover': {
                backgroundColor: theme.palette.action.hover
              }
            }}
          >
            <MoreVertIcon fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={anchorEl}
            open={open}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          >
            <MenuItem onClick={handleEdit}>
              <ListItemIcon>
                <EditIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Edit</ListItemText>
            </MenuItem>
            <MenuItem onClick={handleDelete} sx={{ color: 'error.main' }}>
              <ListItemIcon>
                <DeleteIcon fontSize="small" color="error" />
              </ListItemIcon>
              <ListItemText>Remove</ListItemText>
            </MenuItem>
          </Menu>
        </Box>
      )}

      <CardContent sx={{ pt: 4, pb: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
          {/* Avatar */}
          <Avatar
            src={leader.employee_photo}
            sx={{
              width: 80,
              height: 80,
              fontSize: '1.5rem',
              fontWeight: 600,
              backgroundColor: leader.color_code,
              border: `3px solid ${leader.color_code}`,
              mb: 2
            }}
          >
            {getInitials(leader.employee_name)}
          </Avatar>

          {/* Position Title */}
          <Typography
            variant="caption"
            sx={{
              color: leader.color_code,
              fontWeight: 600,
              textTransform: 'uppercase',
              letterSpacing: 1
            }}
          >
            {leader.position_title}
          </Typography>

          {/* Name */}
          <Typography variant="h6" fontWeight={600} sx={{ mt: 0.5 }}>
            {leader.employee_name}
          </Typography>

          {/* Email */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
            <EmailIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary" noWrap sx={{ maxWidth: 200 }}>
              {leader.employee_email}
            </Typography>
          </Box>

          {/* Department */}
          {leader.department_name && (
            <Chip
              label={leader.department_name}
              size="small"
              variant="outlined"
              sx={{ mt: 1.5, borderRadius: 2 }}
            />
          )}

          {/* Bio */}
          {leader.bio && (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                mt: 2,
                display: '-webkit-box',
                WebkitLineClamp: 2,
                WebkitBoxOrient: 'vertical',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {leader.bio}
            </Typography>
          )}

          {/* Position Level Indicator */}
          <Box
            sx={{
              mt: 2,
              pt: 2,
              borderTop: `1px solid ${theme.palette.divider}`,
              width: '100%'
            }}
          >
            <Typography variant="caption" color="text.secondary">
              {getPositionLevelLabel(leader.position_level)}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
};

export default LeaderCard;
